(() => {
  if (window.BundleDiscountEngine) return;

  const root = () => window.Shopify?.routes?.root || '/';
  const cartUrl = () => window.Theme?.routes?.cart_url ? `${window.Theme.routes.cart_url}.js` : `${root()}cart.js`;
  const updateUrl = () => window.Theme?.routes?.cart_update_url || `${root()}cart/update.js`;

  const normalizeCode = (value) => String(value || '').trim().toLowerCase();

  async function getCart() {
    const response = await fetch(cartUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    const data = await response.json();
    if (!response.ok || data?.status) {
      throw new Error(data?.description || data?.message || 'Unable to load cart');
    }
    return data;
  }

  function activeCodes(cart) {
    if (!Array.isArray(cart?.discount_codes)) return [];
    return cart.discount_codes
      .filter((discount) => discount?.code && discount?.applicable !== false)
      .map((discount) => String(discount.code));
  }

  function codeResult(cart, code) {
    const normalized = normalizeCode(code);
    if (!normalized || !Array.isArray(cart?.discount_codes)) return null;
    return cart.discount_codes.find((discount) => normalizeCode(discount?.code) === normalized) || null;
  }

  async function applyCode(code, sectionIds = []) {
    const cleanCode = String(code || '').trim();
    if (!cleanCode) throw new Error('A Shopify discount code is required');

    const currentCart = await getCart();
    const codes = Array.from(new Set([...activeCodes(currentCart), cleanCode]));
    const body = { discount: codes.join(',') };
    if (sectionIds.length) body.sections = sectionIds;
    body.sections_url = window.location.pathname + window.location.search;

    const response = await fetch(updateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    let cart = await response.json();
    if (!response.ok || cart?.status) {
      throw new Error(cart?.description || cart?.message || 'Discount update failed');
    }

    const result = codeResult(cart, cleanCode);
    const applied = Boolean(result && result.applicable === true);

    if (!applied) {
      const remainingCodes = activeCodes(cart).filter((existingCode) => normalizeCode(existingCode) !== normalizeCode(cleanCode));
      const cleanupBody = { discount: remainingCodes.join(',') };
      if (sectionIds.length) cleanupBody.sections = sectionIds;
      cleanupBody.sections_url = window.location.pathname + window.location.search;

      const cleanupResponse = await fetch(updateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(cleanupBody),
      });
      const cleanedCart = await cleanupResponse.json();
      if (cleanupResponse.ok && !cleanedCart?.status) cart = cleanedCart;
    }

    return { cart, applied, result };
  }

  function itemBundleId(item) {
    return String(item?.properties?._bundle_instance_id || item?.properties?._bundle_id || '');
  }

  function bundleItems(cart, bundleId) {
    const id = String(bundleId || '');
    if (!id || !Array.isArray(cart?.items)) return [];
    return cart.items.filter((item) => itemBundleId(item) === id);
  }

  function lineDiscountAmount(item) {
    const explicit = Number(item?.total_discount || 0);
    const original = Number(item?.original_line_price ?? item?.line_price ?? 0);
    const final = Number(item?.final_line_price ?? item?.line_price ?? original);
    return Math.max(explicit, original - final, 0);
  }

  function automaticApplied(beforeCart, afterCart, bundleId, expectedTitle = '') {
    const title = String(expectedTitle || '').trim().toLowerCase();
    const items = bundleItems(afterCart, bundleId);

    if (title) {
      const cartApplication = Array.isArray(afterCart?.cart_level_discount_applications)
        ? afterCart.cart_level_discount_applications.some((application) =>
            String(application?.title || '').trim().toLowerCase() === title
          )
        : false;

      const lineApplication = items.some((item) => {
        const discounts = Array.isArray(item?.discounts) ? item.discounts : [];
        return discounts.some((discount) => String(discount?.title || '').trim().toLowerCase() === title);
      });

      if (cartApplication || lineApplication) return true;
    }

    if (items.some((item) => lineDiscountAmount(item) > 0)) return true;

    const beforeDiscount = Number(beforeCart?.total_discount || 0);
    const afterDiscount = Number(afterCart?.total_discount || 0);
    return afterDiscount > beforeDiscount;
  }

  async function rollback(bundleId, sectionIds = []) {
    const currentCart = await getCart();
    const updates = {};
    bundleItems(currentCart, bundleId).forEach((item) => {
      if (item?.key) updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) return currentCart;

    const body = { updates };
    if (sectionIds.length) body.sections = sectionIds;
    body.sections_url = window.location.pathname + window.location.search;

    const response = await fetch(updateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const cart = await response.json();
    if (!response.ok || cart?.status) {
      throw new Error(cart?.description || cart?.message || 'Unable to roll back bundle items');
    }
    return cart;
  }

  window.BundleDiscountEngine = {
    getCart,
    applyCode,
    automaticApplied,
    rollback,
    bundleItems,
  };
})();
